// Pairing screen + poll loop (docs/04 §Pairing lifecycle).
//
// Renders the 6-char code full-screen (readable across a room) plus a QR that
// deep-links the CMS pair page with the code prefilled, then polls pair/poll
// every 3 s. Network errors back off exponentially without leaving the screen
// blank. Codes expire (PAIRING_CODE_TTL) so a fresh one is requested well before
// then. Resolves with the device token once an operator binds the code.

import type { PlayerApiLike } from './api.ts';
import type { Config } from './config.ts';
import type { LogBuffer } from './logbuffer.ts';
import type { PairResult } from './types.ts';
import { encodeQr, qrToSvg } from './qr.ts';

const POLL_INTERVAL_MS = 3000;
const MAX_BACKOFF_MS = 30000;
const CODE_REFRESH_MS = 13 * 60 * 1000; // refresh before the 15-min server TTL

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function deviceHint(): string {
  // A human-readable hint the operator sees when binding the code in the CMS.
  const ua = navigator.userAgent;
  const platform = /Android/i.test(ua) ? 'Android' : /CrOS/i.test(ua) ? 'ChromeOS' : 'Browser';
  return `${platform} · ${screen.width}×${screen.height}`;
}

function render(root: HTMLElement, code: string, cmsUrl: string): void {
  const link = `${cmsUrl}/screens/pair?code=${encodeURIComponent(code)}`;
  const svg = qrToSvg(encodeQr(link));
  const cells = code
    .split('')
    .map((ch) => `<span class="code-cell">${ch}</span>`)
    .join('');
  root.innerHTML = `
    <main class="pairing" role="main">
      <div class="pairing-card">
        <h1 class="pairing-title">Pair this screen</h1>
        <p class="pairing-hint">
          Open <strong>SeenCast by SeenLabs</strong> and enter this code, or scan the
          QR to open the pairing page with the code filled in.
        </p>
        <div class="pairing-row">
          <div class="code" aria-label="Pairing code ${code.split('').join(' ')}">${cells}</div>
          <div class="qr" aria-hidden="false">${svg}</div>
        </div>
        <p class="pairing-sub" data-status>Waiting for pairing…</p>
      </div>
    </main>`;
}

function setStatus(root: HTMLElement, text: string): void {
  const el = root.querySelector<HTMLElement>('[data-status]');
  if (el) el.textContent = text;
}

/**
 * Drive the whole pairing flow until success. Never rejects: on any error it
 * backs off and retries, because an unattended screen must recover on its own.
 */
export async function runPairing(
  root: HTMLElement,
  api: PlayerApiLike,
  cfg: Config,
  log: LogBuffer,
): Promise<PairResult> {
  let backoff = POLL_INTERVAL_MS;

  for (;;) {
    // 1. Obtain a fresh code (retry with backoff until the server answers).
    let code: string;
    try {
      code = (await api.pairStart(deviceHint())).code;
      render(root, code, cfg.cmsUrl);
      backoff = POLL_INTERVAL_MS;
    } catch (e) {
      log.add('warn', 'pair_start_failed', String(e));
      setStatus(root, 'Connecting…');
      await sleep(backoff);
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      continue;
    }

    // 2. Poll until paired, the code expires, or too many errors.
    const codeIssuedAt = Date.now();
    for (;;) {
      if (Date.now() - codeIssuedAt > CODE_REFRESH_MS) break; // get a fresh code
      await sleep(POLL_INTERVAL_MS);
      try {
        const result = await api.pairPoll(code);
        if (result) {
          setStatus(root, 'Paired ✓');
          log.add('info', 'paired', undefined);
          return result;
        }
        setStatus(root, 'Waiting for pairing…');
        backoff = POLL_INTERVAL_MS;
      } catch (e) {
        log.add('warn', 'pair_poll_failed', String(e));
        setStatus(root, 'Reconnecting…');
        await sleep(backoff);
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      }
    }
  }
}
